/**
 * Capabilities are merchant/product facing things that the Shopper Agent can do.
 *
 * The optimal way to structure and build skills for Shopper Agent (ie. Redo Skills and their skills registry)
 * is not necessarily how the merchant thinks about what the agent can do. Capabilities are how we bridge that gap.
 */
export enum ShopperAgentCapabilityKind {
  EDIT_ORDER = "edit-order",
  EDIT_ADDRESS = "edit-address",
  CANCEL_ORDER = "cancel-order",
  CREATE_RETURN = "create-return",
  ORDER_TRACKING = "order-tracking",
  DISCOUNTS = "discounts",
  LOYALTY = "loyalty",
  SEARCH_KNOWLEDGE = "search-knowledge",
  ESCALATE_TO_SUPPORT = "escalate-to-support",
  NOTIFY_BACK_IN_STOCK = "notify-back-in-stock",
  HSA_ELIGIBILITY = "hsa-eligibility",
}

/** Merchant-facing copy for a capability. */
export interface ShopperAgentCapabilityCopy {
  /** Sentence case, phrased as something the agent does. */
  name: string;
  /** One line on what it actually does, in the merchant's terms. */
  description: string;
}

/**
 * The single source of capability wording.
 *
 * Lives beside the kinds so adding one forces the copy, and so the merchant
 * playbook and the admin inspector can never call the same capability
 * different things.
 */
export const ShopperAgentCapabilityCopyByKind = {
  [ShopperAgentCapabilityKind.EDIT_ORDER]: {
    name: "Edit an order",
    description:
      "Add items, remove items, or switch variants on an order the customer already placed, without them needing to cancel and re-buy. The agent checks what your rules allow before offering it.",
  },
  [ShopperAgentCapabilityKind.EDIT_ADDRESS]: {
    name: "Change a shipping address",
    description:
      "Update the delivery address on an order that hasn't shipped. The agent confirms the new address with the customer and writes it back to the order.",
  },
  [ShopperAgentCapabilityKind.CANCEL_ORDER]: {
    name: "Cancel an order",
    description:
      "Cancel an order for a customer who asks to call it off, within the window your rules allow, and confirm the refund path.",
  },
  [ShopperAgentCapabilityKind.CREATE_RETURN]: {
    name: "Start a return",
    description:
      "Check what's eligible, then open a return and send the label in the conversation, so the customer never has to find your returns portal.",
  },
  [ShopperAgentCapabilityKind.ORDER_TRACKING]: {
    name: "Track an order",
    description:
      "Answer where an order is with live carrier status, and link the customer to your Redo tracking page instead of a raw carrier site.",
  },
  [ShopperAgentCapabilityKind.DISCOUNTS]: {
    name: "Offer a discount",
    description:
      "Generate a real, single-use discount code mid-conversation when a shopper hesitates, staying inside the discount and proclivity you set in the playbook.",
  },
  [ShopperAgentCapabilityKind.LOYALTY]: {
    name: "Look up loyalty points",
    description:
      "Tell a customer their points balance, what they can redeem it for, and how close they are to the next reward.",
  },
  [ShopperAgentCapabilityKind.SEARCH_KNOWLEDGE]: {
    name: "Answer from your knowledge",
    description:
      "Answer policy and product questions by quoting your own knowledge articles, rather than guessing or deferring to a human.",
  },
  [ShopperAgentCapabilityKind.ESCALATE_TO_SUPPORT]: {
    name: "Hand off to your team",
    description:
      "Hand the conversation to your team when the customer asks for a person or the agent reaches the edge of what it can do, passing the full context along.",
  },
  [ShopperAgentCapabilityKind.NOTIFY_BACK_IN_STOCK]: {
    name: "Sign up for back-in-stock alerts",
    description:
      "Subscribe a shopper to a restock alert for the exact variant they wanted, then let the Back in stock trigger or your own automation do the follow-up.",
  },
  [ShopperAgentCapabilityKind.HSA_ELIGIBILITY]: {
    name: "Answer HSA/FSA eligibility questions",
    description:
      "Check whether the products a shopper asks about can be paid for with HSA/FSA funds, including items that need a Letter of Medical Necessity, and point them to your HSA checkout flow.",
  },
} satisfies Record<ShopperAgentCapabilityKind, ShopperAgentCapabilityCopy>;

/** Derived so there is one place to change a capability's name. */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const ShopperAgentCapabilityFriendlyName = Object.fromEntries(
  Object.entries(ShopperAgentCapabilityCopyByKind).map(([kind, copy]) => [
    kind,
    copy.name,
  ]),
) as Record<ShopperAgentCapabilityKind, string>;
